import { Fragment, ReactNode } from 'react';
import { JSONContent } from '@tiptap/core';
import { Box, Heading, ListItem, OrderedList, Text, UnorderedList, Link } from '@chakra-ui/react';
import { FIND_HIGHLIGHT } from '../../../constants';

// -- renderTiptapContent -------------------------------------------------------
// Renders a dBlock's inner tiptap JSON as static React, with no editor mounted.
// Covers the inline marks and block types the chat editor produces inside a
// message bubble (paragraphs, headings, lists, links). Media and files are
// rendered by the caller as their own turns, not here.

export type TiptapHighlight = {
  query: string;
  // Ordinal of the current match among this node's matching runs, in document
  // order; -1 when the current match is in another node.
  currentRun: number;
};

// Counts matching runs across one node's render pass, so the marker can tell
// which run is current. Valid because the renderer walks runs in the same
// document order as the search extractor (tiptap doc, then option titles).
export type FindRunCursor = { nextRun: number };

// Wrap each case-insensitive occurrence of `query` with a find mark; the
// current run's first occurrence gets the strong treatment, every other hit the
// soft wash. Exported for option pill titles, which render outside the tiptap
// document but are searched too.
export function markFindMatches(
  text: string,
  highlight: TiptapHighlight | undefined,
  runCursor: FindRunCursor,
): ReactNode {
  const query = highlight?.query ?? '';
  if (query === '') {
    return text;
  }
  const haystack = text.toLowerCase();
  let from = haystack.indexOf(query);
  if (from === -1) {
    return text;
  }
  const runIsCurrent = runCursor.nextRun === highlight?.currentRun;
  runCursor.nextRun += 1;

  const parts: ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  let occurrence = 0;
  while (from !== -1) {
    if (from > cursor) {
      parts.push(<Fragment key={key++}>{text.slice(cursor, from)}</Fragment>);
    }
    const to = from + query.length;
    const isCurrentMark = runIsCurrent && occurrence === 0;
    parts.push(
      <Box
        as="mark"
        key={key++}
        bg={isCurrentMark ? FIND_HIGHLIGHT.currentBg : FIND_HIGHLIGHT.otherBg}
        color={isCurrentMark ? FIND_HIGHLIGHT.currentFg : 'inherit'}
        fontWeight={isCurrentMark ? 'semibold' : undefined}
        borderRadius="sm"
        px="1px"
        data-current={isCurrentMark || undefined}
      >
        {text.slice(from, to)}
      </Box>,
    );
    cursor = to;
    occurrence += 1;
    from = haystack.indexOf(query, cursor);
  }
  if (cursor < text.length) {
    parts.push(<Fragment key={key++}>{text.slice(cursor)}</Fragment>);
  }
  return parts;
}

function renderMarks(
  text: string,
  marks: JSONContent['marks'],
  highlight: TiptapHighlight | undefined,
  runCursor: FindRunCursor,
): ReactNode {
  const content = markFindMatches(text, highlight, runCursor);
  if (!marks || marks.length === 0) {
    return content;
  }
  return marks.reduce<ReactNode>((acc, mark) => {
    switch (mark.type) {
      case 'bold':
        return <strong>{acc}</strong>;
      case 'italic':
        return <em>{acc}</em>;
      case 'underline':
        return <u>{acc}</u>;
      case 'strike':
        return <s>{acc}</s>;
      case 'link':
        return (
          <Link
            href={typeof mark.attrs?.href === 'string' ? mark.attrs.href : undefined}
            isExternal
          >
            {acc}
          </Link>
        );
      default:
        return acc;
    }
  }, content);
}

// Positional keys throughout: this is a pure projection of tiptap JSON with no
// stateful children, and any content change replaces node.data wholesale, so
// there is no reconciliation state for a reordered key to corrupt.

function renderInline(
  nodes: JSONContent[] | undefined,
  highlight: TiptapHighlight | undefined,
  runCursor: FindRunCursor,
): ReactNode {
  if (!nodes) {
    return null;
  }
  const rendered: ReactNode[] = [];
  for (const [position, node] of nodes.entries()) {
    if (node.type === 'hardBreak') {
      rendered.push(<br key={position} />);
    } else if (node.type === 'text' && typeof node.text === 'string') {
      rendered.push(
        <Fragment key={position}>
          {renderMarks(node.text, node.marks, highlight, runCursor)}
        </Fragment>,
      );
    }
  }
  return rendered;
}

function renderListItems(
  items: JSONContent[] | undefined,
  highlight: TiptapHighlight | undefined,
  runCursor: FindRunCursor,
): ReactNode[] {
  const rendered: ReactNode[] = [];
  for (const [position, item] of (items ?? []).entries()) {
    rendered.push(
      <ListItem key={position}>{renderTiptapContent(item.content, highlight, runCursor)}</ListItem>,
    );
  }
  return rendered;
}

export function renderTiptapContent(
  nodes: JSONContent[] | undefined,
  highlight?: TiptapHighlight,
  runCursor: FindRunCursor = { nextRun: 0 },
): ReactNode {
  if (!nodes) {
    return null;
  }
  const rendered: ReactNode[] = [];
  for (const [position, node] of nodes.entries()) {
    switch (node.type) {
      case 'paragraph':
        rendered.push(
          <Text key={position}>{renderInline(node.content, highlight, runCursor)}</Text>,
        );
        break;
      case 'heading':
        rendered.push(
          <Heading key={position} size="sm">
            {renderInline(node.content, highlight, runCursor)}
          </Heading>,
        );
        break;
      case 'bulletList':
        rendered.push(
          // Indent so the markers sit inside the bubble, matching the student
          // runtime (globals.css: .content-bubble ul/ol have margin-left 1.25rem).
          <UnorderedList key={position} ml={5} my={0}>
            {renderListItems(node.content, highlight, runCursor)}
          </UnorderedList>,
        );
        break;
      case 'orderedList':
        rendered.push(
          <OrderedList key={position} ml={5} my={0}>
            {renderListItems(node.content, highlight, runCursor)}
          </OrderedList>,
        );
        break;
      default:
        rendered.push(<Box key={position}>{renderInline(node.content, highlight, runCursor)}</Box>);
    }
  }
  return rendered;
}
